import React, { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Grid,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Collapse,
  Alert,
  Checkbox,
  Switch
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelIcon from "@mui/icons-material/Label";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import TagChip from '../Common/TagChip';
import TagsDropdown from '../TagsDropdown';

const ActionsSendAPI = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  
  // API specific settings
  const [apiUrl, setApiUrl] = useState('');
  const [httpMethod, setHttpMethod] = useState('POST');
  const [authType, setAuthType] = useState('none');
  const [authToken, setAuthToken] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [contentType, setContentType] = useState('application/json');
  const [timeout, setTimeout] = useState(30);
  const [includeFileContent, setIncludeFileContent] = useState(false);
  
  // Custom payload settings
  const [useCustomPayload, setUseCustomPayload] = useState(false);
  const [customPayload, setCustomPayload] = useState('');
  
  // Response to tag settings
  const [saveResponseToTag, setSaveResponseToTag] = useState(false);
  const [responseTagName, setResponseTagName] = useState('api_response');
  
  // Headers
  const [headers, setHeaders] = useState([{ key: '', value: '' }]);

  // Tag search and menus
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  
  // Refs for TagEditors
  const urlRef = useRef(null);
  const payloadRef = useRef(null);
  const headerValueRefs = useRef([]);
  const authUsernameRef = useRef(null);
  const authPasswordRef = useRef(null);
  const authTokenRef = useRef(null);
  
  // Advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Additional tags specific to API component
  const additionalTags = useMemo(() => [
    { key: 'tag_fileContent', label: 'File Content', value: 'fileContent', description: 'Full content of the source file (only if Include File Content is enabled)' }
  ], []);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'sendapi',
    onSaveStatusChange,
    customFields: {
      apiUrl: '',
      httpMethod: 'POST',
      authType: 'none',
      authToken: '',
      authUsername: '',
      authPassword: '',
      contentType: 'application/json',
      timeout: 30,
      includeFileContent: false,
      useCustomPayload: false,
      customPayload: '',
      saveResponseToTag: false,
      responseTagName: 'api_response',
      headers: {}
    },
    customComparison: (initial) => {
      const headersObject = {};
      headers.forEach(header => {
        if (header.key.trim() !== '') {
          headersObject[header.key.trim()] = header.value;
        }
      });

      return (
        initial.apiUrl === apiUrl &&
        initial.httpMethod === httpMethod &&
        initial.authType === authType &&
        initial.authToken === authToken &&
        initial.authUsername === authUsername &&
        initial.authPassword === authPassword &&
        initial.contentType === contentType &&
        initial.timeout === timeout &&
        initial.includeFileContent === includeFileContent &&
        initial.useCustomPayload === useCustomPayload &&
        initial.customPayload === customPayload &&
        initial.saveResponseToTag === saveResponseToTag &&
        initial.responseTagName === responseTagName &&
        JSON.stringify(initial.headers || {}) === JSON.stringify(headersObject)
      );
    },
    customSaveData: () => {
      const headersObject = {};
      headers.forEach(header => {
        if (header.key.trim() !== '') {
          headersObject[header.key.trim()] = header.value;
        }
      });

      return {
        apiUrl,
        httpMethod,
        authType,
        authToken,
        authUsername,
        authPassword,
        contentType,
        timeout,
        includeFileContent,
        useCustomPayload,
        customPayload,
        saveResponseToTag,
        responseTagName,
        headers: headersObject,
        useConditions: true,
        conditions
      };
    },
    loadDataFunction: (data) => {
      console.log('Loading SendAPI data:', data);
      
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      setApiUrl(parsedData.apiUrl || '');
      setHttpMethod(parsedData.httpMethod || 'POST');
      setAuthType(parsedData.authType || 'none');
      setAuthToken(parsedData.authToken || '');
      setAuthUsername(parsedData.authUsername || '');
      setAuthPassword(parsedData.authPassword || '');
      setContentType(parsedData.contentType || 'application/json');
      setTimeout(parsedData.timeout !== undefined ? parsedData.timeout : 30);
      setIncludeFileContent(parsedData.includeFileContent || false);
      setUseCustomPayload(parsedData.useCustomPayload || false);
      setCustomPayload(parsedData.customPayload || '');
      setSaveResponseToTag(parsedData.saveResponseToTag || false);
      setResponseTagName(parsedData.responseTagName || 'api_response');
      
      const loadedHeaders = [];
      if (parsedData.headers && typeof parsedData.headers === 'object') {
        Object.entries(parsedData.headers).forEach(([key, value]) => {
          loadedHeaders.push({ key, value });
        });
      }
      if (loadedHeaders.length === 0) {
        loadedHeaders.push({ key: '', value: '' });
      } else {
        loadedHeaders.push({ key: '', value: '' });
      }
      setHeaders(loadedHeaders);
    },
    enableConditions: true
  });

  const handleMenuOpen = (event, type) => {
    setMenuType(type);
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    if (menuType === 'url') {
      urlRef.current?.insertTag(value);
    } else if (menuType === 'payload') {
      payloadRef.current?.insertTag(value);
    } else if (menuType === 'authUsername') {
      authUsernameRef.current?.insertTag(value);
    } else if (menuType === 'authPassword') {
      authPasswordRef.current?.insertTag(value);
    } else if (menuType === 'authToken') {
      authTokenRef.current?.insertTag(value);
    } else if (menuType.startsWith('header_')) {
      const index = parseInt(menuType.split('_')[1]);
      headerValueRefs.current[index]?.insertTag(value);
    }
    setAnchorEl(null);
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    
    if (index === headers.length - 1 && 
        (newHeaders[index].key.trim() !== '' || newHeaders[index].value.trim() !== '')) {
      newHeaders.push({ key: '', value: '' });
    }
    
    setHeaders(newHeaders);
  };

  const handleRemoveHeader = (index) => {
    if (headers.length > 1) {
      setHeaders(headers.filter((_, i) => i !== index));
    } else {
      setHeaders([{ key: '', value: '' }]);
    }
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('settings.sendapi_settings', 'Send to API Configuration')}
      isSaveDisabled={isSaveDisabled}
      onSave={baseHandleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid item xs={12}>
        <Card sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden',
        }}>
          <CardHeader
            title={
              <Typography sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box component="span" sx={{
                  bgcolor: '#5757b0',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                }}>
                  ENDPOINT
                </Box>
                {t('settings.api_endpoint', 'API Endpoint')}
              </Typography>
            }
            sx={{
              borderBottom: '1px solid #f0f0f8',
              backgroundColor: '#fafbff',
              p: { xs: 1.5, sm: 2.5 },
            }}
          />
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                {t('settings.api_url', 'API URL')}
              </Typography>
              <TagEditor
                    caseid={caseid}
                ref={urlRef}
                value={apiUrl}
                onChange={setApiUrl}
                placeholder="https://api.example.com/endpoint"
                singleLine
                onTagClick={(e) => handleMenuOpen(e, 'url')}
                tags={tags}
              />
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>{t('settings.http_method', 'HTTP Method')}</InputLabel>
                  <Select
                    value={httpMethod}
                    onChange={(e) => setHttpMethod(e.target.value)}
                    label={t('settings.http_method', 'HTTP Method')}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>{t('settings.content_type', 'Content Type')}</InputLabel>
                  <Select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    label={t('settings.content_type', 'Content Type')}
                  >
                    <MenuItem value="application/json">application/json</MenuItem>
                    <MenuItem value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</MenuItem>
                    <MenuItem value="multipart/form-data">multipart/form-data</MenuItem>
                    <MenuItem value="text/plain">text/plain</MenuItem>
                    <MenuItem value="text/html">text/html</MenuItem>
                    <MenuItem value="application/xml">application/xml</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={includeFileContent}
                  onChange={(e) => setIncludeFileContent(e.target.checked)}
                />
              }
              label={t('settings.include_file_content', 'Include file content')}
              sx={{ mb: 2 }}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <FormControlLabel
                control={
                  <Switch 
                    checked={saveResponseToTag}
                    onChange={(e) => setSaveResponseToTag(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t('settings.save_response_to_tag', 'Save Response to Tag')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.save_response_to_tag_desc', 'Store response in a custom tag')}
                    </Typography>
                  </Box>
                }
              />
              
              {saveResponseToTag && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <TextField
                    fullWidth
                    label={t('settings.response_tag_name', 'Response Tag Name')}
                    variant="outlined"
                    value={responseTagName}
                    onChange={(e) => setResponseTagName(e.target.value)}
                    placeholder="api_response"
                    required
                    helperText={
                      <Box component="span">
                        {t('settings.response_tag_name_help', 'Reference as')} <TagChip tagName={`{${responseTagName}}`} sx={{ fontSize: '0.7rem', padding: '1px 4px' }} />
                      </Box>
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LabelIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {t('settings.authentication', 'Authentication')}
          </Typography>
          <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('settings.auth_type', 'Authentication Type')}</InputLabel>
            <Select
              value={authType}
              onChange={(e) => setAuthType(e.target.value)}
              label={t('settings.auth_type', 'Authentication Type')}
            >
              <MenuItem value="none">{t('settings.none', 'None')}</MenuItem>
              <MenuItem value="basic">{t('settings.basic_auth', 'Basic Auth')}</MenuItem>
              <MenuItem value="bearer">{t('settings.bearer_token', 'Bearer Token')}</MenuItem>
              <MenuItem value="token">{t('settings.api_key', 'API Key')}</MenuItem>
            </Select>
          </FormControl>
          
          {authType === 'basic' && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                 <TagEditor
                    caseid={caseid}
                    ref={authUsernameRef}
                    value={authUsername}
                    onChange={setAuthUsername}
                    placeholder={t('settings.username', "Username")}
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'authUsername')}
                    tags={tags}
                  />
              </Grid>
              <Grid item xs={6}>
                 <TagEditor
                    caseid={caseid}
                    ref={authPasswordRef}
                    value={authPassword}
                    onChange={setAuthPassword}
                    placeholder={t('settings.password', "Password")}
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'authPassword')}
                    tags={tags}
                  />
              </Grid>
            </Grid>
          )}
          
          {(authType === 'bearer' || authType === 'token') && (
            <Box sx={{ mb: 2 }}>
                <TagEditor
                    caseid={caseid}
                    ref={authTokenRef}
                    value={authToken}
                    onChange={setAuthToken}
                    placeholder={authType === 'bearer' ? t('settings.bearer_token', "Bearer Token") : t('settings.api_key', "API Key")}
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'authToken')}
                    tags={tags}
                />
            </Box>
          )}
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <Box 
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            mb: 2,
            color: '#5757b0',
            fontWeight: 600
          }}
        >
          <Typography variant="subtitle1">
            {t('settings.advanced_options', 'Advanced Options')}
          </Typography>
          {showAdvancedOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        
        <Collapse in={showAdvancedOptions}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              {t('settings.custom_headers', 'Custom Headers')}
            </Typography>
            
            {headers.map((header, index) => (
              <Box key={index} display="flex" alignItems="center" sx={{ mb: 1 }}>
                <TextField
                  label={t('settings.header_name', "Name")}
                  variant="outlined"
                  size="small"
                  value={header.key}
                  onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                  sx={{ mr: 1, flexBasis: '30%' }}
                />
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                  <TagEditor
                    caseid={caseid}
                    ref={el => headerValueRefs.current[index] = el}
                    value={header.value}
                    onChange={(val) => handleHeaderChange(index, 'value', val)}
                    placeholder={t('settings.header_value', "Value")}
                    style={{ flexGrow: 1 }}
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, `header_${index}`)}
                    tags={tags}
                  />
                  <IconButton onClick={() => handleRemoveHeader(index)}>
                    <DeleteOutlineIcon color="error" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            
            <Box sx={{ mt: 2 }}>
              <TextField
                label={t('settings.timeout', "Timeout (s)")}
                variant="outlined"
                type="number"
                size="small"
                value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value) || 30)}
                sx={{ width: 120 }}
              />
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={useCustomPayload} 
                  onChange={(e) => setUseCustomPayload(e.target.checked)}
                />
              }
              label={t('settings.use_custom_payload', 'Custom Payload')}
            />
            
            {useCustomPayload && (
              <Box sx={{ mt: 1 }}>
                <TagEditor
                    caseid={caseid}
                  ref={payloadRef}
                  value={customPayload}
                  onChange={setCustomPayload}
                  multiline
                  minRows={4}
                  placeholder={t('settings.enter_custom_payload', 'Enter JSON payload')}
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'payload')}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Grid>

      <TagsDropdown
        tags={tags}
        additionalTags={additionalTags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsSendAPI);
